import React from "react";
import { Commend3Strategy } from "../Types";
import { isStrategyGenerated, resolveGenerationLine } from "../Commend3Helper";
import {
  PREVIEW_DISABLED_REASON,
  STRATEGY_SECTION_LABEL,
  SUBSTITUTION_LOCKED_NOTE,
} from "../Commend3Constants";

type StrategyPickerProps = {
  strategies: Commend3Strategy[];
  selectedStrategyId: string | null;
  locked: boolean;
  substitutionOnly: boolean;
  onSelect: (strategy: Commend3Strategy) => void;
  onPreview: (strategy: Commend3Strategy) => void;
};

const TONE_COLORS: Record<string, string> = {
  fresh: "#109648",
  stale: "#D97706",
  muted: "#B4B9D4",
};

const StrategyPicker = ({
  strategies,
  selectedStrategyId,
  locked,
  substitutionOnly,
  onSelect,
  onPreview,
}: StrategyPickerProps) => {
  const selected =
    strategies.find((strategy) => strategy.uid === selectedStrategyId) ?? null;
  const description = selected?.tool_tip_message?.trim();
  const line = selected ? resolveGenerationLine(selected) : null;
  const previewOff = !selected || !isStrategyGenerated(selected);
  const selectDisabled = substitutionOnly || locked;

  return (
    <div
      id="commend3-strategy-picker"
      className="tw-flex tw-flex-col tw-gap-3 tw-rounded-[10px] tw-p-5"
      style={{ background: "#F4F5F9", border: "1px solid #E3E5EC" }}
    >
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
        <span className="tw-text-[16px] tw-font-semibold tw-text-[#0B0C23]">
          {STRATEGY_SECTION_LABEL} <span style={{ color: "#D60027" }}>*</span>
        </span>
        <span
          className="tw-inline-flex tw-shrink-0"
          title={previewOff ? PREVIEW_DISABLED_REASON : undefined}
        >
          <button
            id="commend3-config-preview"
            type="button"
            className="tw-inline-flex tw-h-10 tw-items-center tw-justify-center tw-rounded-[7px] tw-bg-white tw-px-5 tw-text-[14px] tw-font-medium tw-text-[#0B0C23]"
            style={{
              border: "1px solid #E3E5EC",
              opacity: previewOff ? 0.45 : 1,
              cursor: previewOff ? "not-allowed" : "pointer",
            }}
            disabled={previewOff}
            onClick={() => selected && onPreview(selected)}
          >
            Preview
          </button>
        </span>
      </div>

      <select
        id="commend3-config-strategy-select"
        className="tw-h-11 tw-w-full tw-rounded-[7px] tw-px-[13px] tw-text-[14.5px]"
        style={{
          maxWidth: "640px",
          border: "1px solid #E3E5EC",
          color: locked ? "#59596E" : "#0B0C23",
          background: locked ? "#F4F5F9" : "#fff",
          cursor: selectDisabled ? "not-allowed" : "pointer",
          opacity: selectDisabled ? 0.75 : 1,
        }}
        value={selectedStrategyId ?? ""}
        disabled={selectDisabled}
        onChange={(event) => {
          const next = strategies.find((strategy) => strategy.uid === event.target.value);
          if (next) onSelect(next);
        }}
        aria-label={STRATEGY_SECTION_LABEL}
      >
        {strategies.map((strategy) => (
          <option key={strategy.uid} value={strategy.uid}>
            {strategy.name}
          </option>
        ))}
      </select>

      {description ? (
        <div className="tw-text-[14.5px] tw-text-[#59596E]" style={{ lineHeight: 1.55 }}>
          {description}
        </div>
      ) : null}

      {line ? (
        <div className="tw-text-[13.5px]">
          <span
            style={{ color: TONE_COLORS[line.tone] ?? TONE_COLORS.muted, fontWeight: 500 }}
          >
            {line.text.replace("Last generated: ", "Generated ")}
          </span>
        </div>
      ) : null}

      {substitutionOnly ? (
        <div className="tw-text-[13.5px] tw-text-[#59596E]" style={{ lineHeight: 1.55 }}>
          {SUBSTITUTION_LOCKED_NOTE}
        </div>
      ) : null}
    </div>
  );
};

export default StrategyPicker;
